import React from 'react';
import {
	faArrowToBottom,
	faTimes,
	faTrashAlt,
	type IconDefinition,
} from '@netx/core-icons';
import {Divider, Skeleton, Typography} from '@mui/material';
import {ActionList} from '@netx/core-components/ActionList';
import {IconButton} from '@netx/core-components/IconButton';
import type {CustomVirtualizedSelection} from '@netx/core-components/TableNext/types';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {
	getTableManagerHeaderRowUtilityClass,
	TABLE_MANAGER_HEADER_ROW_CLASS_NAME,
	tableManagerHeaderRowClasses,
	type TableManagerHeaderRowClasses,
} from './tableManagerHeaderClasses';
import {ReactAction} from '@netx/core/bootstrap';

type TableManagerHeaderOwnerState = WithClasses<TableManagerHeaderRowClasses>;

const useUtilityClasses = (ownerState: TableManagerHeaderOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getTableManagerHeaderRowUtilityClass, classes);
};

const TableManagerHeaderRoot = styled('div', {
	name: TABLE_MANAGER_HEADER_ROW_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	alignItems: 'center',
	flex: '0 0 auto',
	padding: theme.spacing(1),
	background: theme.palette.custom.background.accent,
}));

export interface TableManagerHeaderProps extends WithClasses<TableManagerHeaderRowClasses> {
	clearLabel?: string;
	deleteIcon?: IconDefinition;
	deleteLabel?: string;
	exportIcon?: IconDefinition;
	exportLabel?: string;
	disabled?: boolean;
	loading?: boolean;
	totalRows: number;
	rowSelection: CustomVirtualizedSelection;
	title?: string;
	customActions?:
		| React.ReactNode
		| [(ReactAction[] | ReactAction)[], (action: ReactAction) => void]; // Array of actions or array of action render functions
	onDelete?: () => void;
	onExport?: () => void;
	onClearSelection: () => void;
}

export const TableManagerHeader = React.forwardRef<
	HTMLDivElement,
	TableManagerHeaderProps
>(
	(
		{
			clearLabel,
			disabled,
			loading,
			deleteIcon,
			deleteLabel,
			exportIcon,
			exportLabel,
			rowSelection,
			title,
			totalRows,
			customActions,
			onDelete,
			onExport,
			onClearSelection,
			...props
		}: TableManagerHeaderProps,
		ref,
	) => {
		const totalRowsSelected = rowSelection.all
			? totalRows - rowSelection.excludeIds.length
			: rowSelection.includeIds.length;
		return (
			//consolidate
			<TableManagerHeaderRoot ref={ref} {...props}>
				<IconButton
					// disabled={totalRowsSelected === 0}
					icon={faTimes}
					size="small"
					onClick={onClearSelection}
					sx={{mr: 1}}
					edge="start"
					label={clearLabel ?? i18n.t('i18n.clearSelection')}
				/>
				<Typography variant="body2" noWrap sx={{flex: '1 1 auto'}}>
					{loading ? (
						<Skeleton width={100} variant="text" />
					) : (
						<>{title ?? i18n.t('i18n.nSelected', {count: totalRowsSelected})}</>
					)}
				</Typography>
				<Divider orientation="vertical" flexItem sx={{ml: 2}} />
				{customActions &&
					(customActions instanceof Array ? (
						<div
							style={{
								marginLeft: 4,
								display: 'flex',
								alignItems: 'center',
								height: '100%',
								gap: 8,
							}}
						>
							<ActionList
								actions={customActions[0]}
								onAction={customActions[1]}
								size="small"
							/>
						</div>
					) : (
						customActions
					))}
				{onExport && (
					<IconButton
						disabled={totalRowsSelected === 0}
						icon={faArrowToBottom}
						onClick={() => onExport()}
						size="small"
						label={exportLabel ?? i18n.t('i18n.exportSelected')}
						sx={{ml: 1}}
					/>
				)}
				{onDelete && (
					<IconButton
						disabled={totalRowsSelected === 0}
						icon={deleteIcon ?? faTrashAlt}
						onClick={() => onDelete()}
						size="small"
						label={deleteLabel ?? i18n.t('i18n.deleteSelected')}
						sx={{ml: 1}}
					/>
				)}
			</TableManagerHeaderRoot>
		);
	},
);

export default TableManagerHeader;
